import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { tokens } from '../design/tokens';
import { useApi, axios, API_BASE_URL } from '../hooks/useApi';

interface StaticPageProps {
  title: string;
  content: string;
  lastUpdated?: Date;
  metaDescription?: string;
  className?: string;
}

interface PageData {
  id: number;
  title: string;
  content: string;
  lastUpdated?: string;
  metaDescription?: string;
}

// Componente para renderizar loading
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: ${tokens.spacing.xxxl};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${tokens.colors.surfaceMuted};
  border-top: 4px solid ${tokens.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: ${tokens.spacing.lg};

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${tokens.colors.text};
  font-size: ${tokens.typography.body.size};
  margin: 0;
`;

// Componente para renderizar erro
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: ${tokens.spacing.xxxl};
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  color: ${tokens.colors.error};
  margin-bottom: ${tokens.spacing.lg};
`;

const ErrorText = styled.p`
  color: ${tokens.colors.error};
  font-size: ${tokens.typography.body.size};
  font-weight: ${tokens.typography.body.weight};
  margin: 0 0 ${tokens.spacing.lg} 0;
`;

const RetryButton = styled.button`
  background: ${tokens.colors.primary};
  color: ${tokens.colors.textOnDark};
  border: none;
  padding: ${tokens.spacing.md} ${tokens.spacing.xl};
  border-radius: ${tokens.radius.md};
  font-size: ${tokens.typography.body.size};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${tokens.colors.secondary};
    transform: translateY(-1px);
  }
`;

// Container principal da página
const PageContainer = styled.div`
  max-width: 1024px; /* 4xl equivalente */
  margin: 0 auto;
  padding: ${tokens.spacing.xl};
  background: ${tokens.colors.surface};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.md};

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing.lg};
    margin: ${tokens.spacing.sm};
    border-radius: ${tokens.radius.md};
  }
`;

// Breadcrumbs
const Breadcrumbs = styled.nav`
  margin-bottom: ${tokens.spacing.xl};
  
  @media (max-width: ${tokens.breakpoints.sm}) {
    margin-bottom: ${tokens.spacing.lg};
  }
`;

const BreadcrumbList = styled.ol`
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.caption.size};
  }
`;

const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;
  
  &:not(:last-child)::after {
    content: '>';
    margin: 0 ${tokens.spacing.sm};
    color: ${tokens.colors.surfaceMuted};
  }
`;

const BreadcrumbLink = styled.a`
  color: ${tokens.colors.primary};
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: ${tokens.colors.secondary};
    text-decoration: underline;
  }
`;

const BreadcrumbCurrent = styled.span`
  color: ${tokens.colors.text};
  font-weight: 600;
`;

// Header da página
const PageHeader = styled.header`
  margin-bottom: ${tokens.spacing.xxxl};
  border-bottom: 2px solid ${tokens.colors.border};
  padding-bottom: ${tokens.spacing.xl};

  @media (max-width: ${tokens.breakpoints.sm}) {
    margin-bottom: ${tokens.spacing.xl};
    padding-bottom: ${tokens.spacing.lg};
  }
`;

const PageTitle = styled.h1`
  font-size: ${tokens.typography.h1.size};
  line-height: ${tokens.typography.h1.line};
  font-weight: ${tokens.typography.h1.weight};
  color: ${tokens.colors.text};
  margin: 0 0 ${tokens.spacing.md} 0;

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: 28px;
    line-height: 36px;
  }
`;

const LastUpdated = styled.time`
  color: ${tokens.colors.surfaceMuted};
  font-size: ${tokens.typography.caption.size};
  display: block;
  margin-top: ${tokens.spacing.sm};
`;

// Conteúdo da página
const PageContent = styled.div`
  color: ${tokens.colors.text};
  line-height: ${tokens.typography.body.line};
  
  /* Estilos para conteúdo HTML */
  h1, h2, h3, h4, h5, h6 {
    color: ${tokens.colors.text};
    margin-top: ${tokens.spacing.xl};
    margin-bottom: ${tokens.spacing.md};
    
    &:first-child {
      margin-top: 0;
    }
  }

  h1 {
    font-size: ${tokens.typography.h2.size};
    line-height: ${tokens.typography.h2.line};
    font-weight: ${tokens.typography.h2.weight};
  }

  h2 {
    font-size: ${tokens.typography.h3.size};
    line-height: ${tokens.typography.h3.line};
    font-weight: ${tokens.typography.h3.weight};
  }

  h3 {
    font-size: ${tokens.typography.h3.size};
    line-height: ${tokens.typography.h3.line};
    font-weight: 600;
  }

  p {
    margin-bottom: ${tokens.spacing.lg};
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul, ol {
    margin-bottom: ${tokens.spacing.lg};
    padding-left: ${tokens.spacing.xl};
  }

  li {
    margin-bottom: ${tokens.spacing.sm};
  }

  a {
    color: ${tokens.colors.primary};
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s ease;

    &:hover {
      border-bottom-color: ${tokens.colors.primary};
      text-decoration: underline;
    }
  }

  strong, b {
    font-weight: 700;
    color: ${tokens.colors.text};
  }

  em, i {
    font-style: italic;
  }

  blockquote {
    border-left: 4px solid ${tokens.colors.primary};
    padding-left: ${tokens.spacing.lg};
    margin: ${tokens.spacing.xl} 0;
    background: ${tokens.colors.surfaceMuted};
    padding: ${tokens.spacing.lg};
    border-radius: 0 ${tokens.radius.md} ${tokens.radius.md} 0;
  }

  code {
    background: ${tokens.colors.surfaceMuted};
    padding: ${tokens.spacing.xs} ${tokens.spacing.sm};
    border-radius: ${tokens.radius.xs};
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: ${tokens.typography.caption.size};
  }

  pre {
    background: ${tokens.colors.surfaceMuted};
    padding: ${tokens.spacing.lg};
    border-radius: ${tokens.radius.md};
    overflow-x: auto;
    margin: ${tokens.spacing.lg} 0;

    code {
      background: none;
      padding: 0;
    }
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.body.size};
    
    h1, h2, h3, h4, h5, h6 {
      margin-top: ${tokens.spacing.lg};
    }
  }
`;

// Footer com informações de contato
const PageFooter = styled.footer`
  margin-top: ${tokens.spacing.xxxl};
  padding-top: ${tokens.spacing.xl};
  border-top: 1px solid ${tokens.colors.border};
  text-align: center;

  @media (max-width: ${tokens.breakpoints.sm}) {
    margin-top: ${tokens.spacing.xl};
    padding-top: ${tokens.spacing.lg};
  }
`;

const FooterContent = styled.div`
  color: ${tokens.colors.surfaceMuted};
  font-size: ${tokens.typography.caption.size};
`;

const FooterLink = styled.a`
  color: ${tokens.colors.primary};
  text-decoration: none;
  margin: 0 ${tokens.spacing.sm};

  &:hover {
    text-decoration: underline;
  }
`;

// Componente principal
const StaticPage: React.FC<StaticPageProps> = ({
  title,
  content,
  lastUpdated,
  metaDescription,
  className
}) => {
  const [isClient, setIsClient] = useState(false);

  // Configurar meta tags dinamicamente
  useEffect(() => {
    setIsClient(true);

    // Configurar meta description
    if (metaDescription) {
      let metaTag = document.querySelector('meta[name="description"]') as HTMLMetaElement;
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', 'description');
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', metaDescription);
    }

    // Configurar title da página
    document.title = `${title} | Café Tutoriales`;

    // Cleanup
    return () => {
      if (metaDescription) {
        const metaTag = document.querySelector('meta[name="description"]');
        if (metaTag) {
          metaTag.remove();
        }
      }
    };
  }, [title, metaDescription]);

  // Formatear fecha para español
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Função para obter breadcrumb baseado no título
  const getBreadcrumbs = () => {
    const currentPath = window.location.pathname;
    const breadcrumbMap: Record<string, { name: string; path: string }> = {
      '/politica-privacidade': { name: 'Política de Privacidad', path: '/politica-privacidade' },
      '/termos-uso': { name: 'Términos de Uso', path: '/termos-uso' },
      '/ajuda-suporte': { name: 'Ayuda y Soporte', path: '/ajuda-suporte' },
      // Fallback para URLs antigas
      '/privacy': { name: 'Política de Privacidad', path: '/politica-privacidade' },
      '/terms': { name: 'Términos de Uso', path: '/termos-uso' },
      '/help': { name: 'Ayuda y Soporte', path: '/ajuda-suporte' }
    };

    const currentPage = breadcrumbMap[currentPath];
    if (!currentPage) return [];

    return [
      { name: 'Inicio', path: '/' },
      currentPage
    ];
  };

  const breadcrumbs = getBreadcrumbs();

  if (!isClient) {
    return null; // SSR fallback
  }

  return (
    <PageContainer className={className}>
      <Breadcrumbs aria-label="Breadcrumb">
        <BreadcrumbList>
          {breadcrumbs.map((item, index) => (
            <BreadcrumbItem key={item.path}>
              {index === breadcrumbs.length - 1 ? (
                <BreadcrumbCurrent>{item.name}</BreadcrumbCurrent>
              ) : (
                <BreadcrumbLink href={item.path}>{item.name}</BreadcrumbLink>
              )}
            </BreadcrumbItem>
          ))}
        </BreadcrumbList>
      </Breadcrumbs>

      <PageHeader>
        <PageTitle>{title}</PageTitle>
        {lastUpdated && (
          <LastUpdated dateTime={lastUpdated.toISOString()}>
            Última actualización: {formatDate(lastUpdated)}
          </LastUpdated>
        )}
      </PageHeader>

      <PageContent
        dangerouslySetInnerHTML={{ __html: content }}
        aria-label={`Contenido de ${title}`}
      />

      <PageFooter>
        <FooterContent>
          ¿Necesitas ayuda?{' '}
          <FooterLink href="/ajuda-suporte">visita nuestro Centro de Ayuda</FooterLink>
          o{' '}
          <FooterLink href="mailto:suporte@cafetutoriales.com">contáctanos por correo</FooterLink>
        </FooterContent>
      </PageFooter>
    </PageContainer>
  );
};

export default StaticPage;

// Hook para usar páginas estáticas via API
export const useStaticPage = (pageType: 'privacy' | 'terms' | 'help') => {
  const apiCall = useCallback(
    async () => {
      return axios.get(`${API_BASE_URL}/social/pages/${pageType}`);
    },
    [pageType]
  );

  return useApi<PageData>(apiCall, {
    immediate: true,
    dependencies: [pageType]
  });
};

// Componente de página completa com dados da API
interface StaticPageWithDataProps {
  pageType: 'privacy' | 'terms' | 'help';
  className?: string;
}

export const StaticPageWithData: React.FC<StaticPageWithDataProps> = ({
  pageType,
  className
}) => {
  const { data, loading, error, execute } = useStaticPage(pageType);

  if (loading) {
    return (
      <LoadingContainer role="status" aria-label="Cargando contenido">
        <LoadingSpinner />
        <LoadingText>Cargando {pageType === 'privacy' ? 'Política de Privacidad' : pageType === 'terms' ? 'Términos de Uso' : 'Ayuda y Soporte'}...</LoadingText>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer role="alert">
        <ErrorIcon>⚠️</ErrorIcon>
        <ErrorText>Error al cargar el contenido: {error}</ErrorText>
        <RetryButton onClick={execute}>
          Intentar de nuevo
        </RetryButton>
      </ErrorContainer>
    );
  }

  if (!data) {
    return (
      <ErrorContainer role="alert">
        <ErrorIcon>📄</ErrorIcon>
        <ErrorText>Contenido no encontrado.</ErrorText>
      </ErrorContainer>
    );
  }

  return (
    <StaticPage
      title={data.title}
      content={data.content}
      lastUpdated={data.lastUpdated ? new Date(data.lastUpdated) : undefined}
      metaDescription={data.metaDescription}
      className={className}
    />
  );
};